import { Table } from '@dynamika-shared/ui/Table/Table';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { usePaginationAndSort } from '@restore/helpers';
import { useEffect, useState, useCallback } from 'react';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { Dropdown } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { FilterDetailedIcon } from '@dynamika-shared/ui/icons';
import { useReadingCompletedFilters, AvailableColumnsType } from './useReadingCompletedFilters';
import { replacePathParams } from '@utils/replacePathWithParams';
import { PaginatedResponse } from '@dynamika-shared/types/general';
import { fetchViewerTasks } from '@api/viewerTask';
import { Reading } from '@dynamika-shared/types/readings.types';
import { UserRoles } from '@dynamika-shared/types/auth';
import { getRouteByRole } from 'src/utils/routeHelpers';
import { ExportCsvButton } from '@components/ExportCsvButton';
import { useCsvExport, toCsvColumns } from '@hooks/useCsvExport';
import { useIsSponsor } from '@hooks/useIsSponsor';

interface ReadingCompletedLayoutProps {
  clinicalTrialId: string;
  listName: 'readingCompleted' | 'externalUserReadingCompleted';
  userRole: UserRoles;
}

export const ReadingCompletedLayout: React.FC<ReadingCompletedLayoutProps> = ({
  clinicalTrialId,
  listName,
  userRole,
}) => {
  const { sortBy, setSortBy, currentPage, currentPageSize, setCurrentPage } =
    usePaginationAndSort(listName);

  const [tasksData, setTasksData] = useState<PaginatedResponse<Reading> | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const {
    displayFilters,
    filteredTableColumns,
    selectColumns,
    searchValue,
    setSearchValue,
    debouncedSearchValue
  } = useReadingCompletedFilters();

  const isSponsor = useIsSponsor();
  const { exportCsv, isExporting } = useCsvExport<Reading>();

  const handleExport = () =>
    exportCsv({
      columns: toCsvColumns(filteredTableColumns),
      rows: tasksData?.content ?? [],
      fileNamePrefix: 'Dynamika_ReadingCompleted',
    });

  const pagination = useMakePagination(
    listName,
    !!tasksData?.content?.length
  );

  const onSort: OnSortColumnType = (name, sortState) => {
    if (clinicalTrialId) {
      const newSort = sortState ? `${name},${sortState}` : undefined;
      setSortBy({ listName, newSortBy: newSort });
      setCurrentPage({ listName, newPage: 1 });
    }
  };

  const fetchTasksData = useCallback(async () => {
    if (!clinicalTrialId) return;

    setLoading(true);
    try {
      const response = await fetchViewerTasks(
        clinicalTrialId,
        {
          search: debouncedSearchValue || undefined,
          sort: sortBy
        },
        currentPage,
        currentPageSize
      );
      setTasksData(response.data);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [clinicalTrialId, debouncedSearchValue, currentPage, currentPageSize, sortBy]);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    if (currentPage !== 1) {
      setCurrentPage({ listName, newPage: 1 });
    }
  }, [currentPage, setCurrentPage, setSearchValue, listName]);

  const delayedSearch = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage({ listName, newPage: 1 });
    }
  }, [currentPage, setCurrentPage, listName]);

  useEffect(() => {
    setSortBy({
      listName: listName,
      newSortBy: 'finishReadingTime,desc',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTasksData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, currentPage, currentPageSize, sortBy, debouncedSearchValue]);

  const getReadingTaskURL = useCallback(
    (row: Reading) => {
      const isViewer = row?.viewerEmbedded;
      const path = isViewer
        ? getRouteByRole(userRole, 'taskViewer')
        : getRouteByRole(userRole, 'taskPlain');

      const url = `${replacePathParams(path, {
        id: clinicalTrialId,
        taskId: row.id,
      })}?status=COMPLETED`;

      return { url };
    },
    [clinicalTrialId, userRole]
  );

  return (
    <div>
      <div className='flex items-start justify-end gap-2 mb-4 mt-1'>
        <div className=''>
          <Input
            value={searchValue}
            onChange={e => {
              const searchVal = e.target.value.trim();
              setSearchValue(searchVal);
              delayedSearch();
            }}
            className='mini:h-[3.2rem] mini:min-w-[24rem] mini:px-1 !mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2'
            placeholder='Type Subject ID'
            suffix={
              <SearchSuffix
                disabled={!searchValue}
                action={handleClearSearch}
              />
            }
            data-testid={'readingCompletedSearch'}
          />
        </div>

        <div
          role='button'
          className='mini:h-[3rem] hover:bg-waterLight flex h-[4rem] cursor-pointer items-center rounded-md'
          data-testid={'readingCompletedFilter'}
        >
          <Dropdown
            onClick={e => selectColumns(e.key as keyof AvailableColumnsType)}
            items={displayFilters}
            trigger={['click']}
            open={visible}
            className='h-full p-1'
            onOpenChange={(_, { source }) => {
              if (source === 'trigger') setVisible(!visible);
            }}
          >
            <div className='flex items-center justify-center gap-1'>
              <FilterDetailedIcon height={20} width={20} />
              <div className='text-1424'>Display</div>
            </div>
          </Dropdown>
        </div>

        {isSponsor && (
          <ExportCsvButton
            onExport={handleExport}
            isExporting={isExporting}
            disabled={!tasksData?.content?.length}
          />
        )}
      </div>

      <Table
        loading={loading}
        columns={filteredTableColumns}
        noContentMessage={
          <NoContent
            iconName={'noContent'}
            title={
              <span className='text-1424 text-spanishGray'>
                <span className='font-semibold text-chineseBlack'>
                  No results found
                </span>
              </span>
            }
          >
            <div />
          </NoContent>
        }
        data={tasksData?.content || []}
        totalItems={tasksData?.totalElements ?? 0}
        onSort={onSort}
        loadingLength={8}
        sortState={sortBy}
        pagination={pagination}
        onRowClick={row => {
          if (clinicalTrialId && typeof row !== 'number') {
            const { url } = getReadingTaskURL(row);
            window.location.assign(url);
          }
        }}
      />
    </div>
  );
};
